import { GzButtonWebComponent } from '../../../ui/gzButton';
import { translateFn } from '../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { showToast } from '../../../tagsManager/helpers/commonFunctions';
import { ToastActionButtonMode, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { html, nothing, TemplateResult } from 'lit';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { getTextForScore, renderRiskScore } from '../../common/customRenderers';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import {
  Actions,
  ActionsEvents,
  Compliance,
  FindingsDetailsItemsType,
  InlineMenuExtraDetailsSelectEvents,
  MitigationType,
  OS_CLASS,
  State,
  Status,
  Watchlist,
} from '../../common/constants';
import { OperatingSystemType } from '@nirvana/shared/dtos/types/protected-entities/common';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { navigateToTasksSection } from '../../../utils/navigation.utils';
import { getBulletColorForScore } from '../../common/utils';
import {
  DeviceDetailsChangedEvent,
  DeviceExtraDetailsChangedEvent,
  ShowExtraDataEventDetails,
} from '../components/deviceDetails/types/types';
import { ImageState } from '../../../ui/gzImageRepo/types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ButtonFill, LinkColor, LinkType, Target } from '../../../ui/gzLinkComponent/types';
import moment from 'moment';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzIconWebComponent } from '../../../ui/gzIcon';
import { ButtonColor } from 'apps/webcomponents/src/ui/gzIconButton/types';
import { entityIconSet } from 'apps/webcomponents/src/theme/entity-icons';

export enum RiskManagementSideMenuEvents {
  VIEW_DEVICES_BY_MISCONFIGURATION = 'view-devices-by-misconfiguration',
  VIEW_USERS_BY_HUMAN_RISKS = 'view-users-by-human-risks',
  VIEW_USERS_BY_DEVICE = 'view-users-by-device',
  VIEW_MISCONFIGURATION_DETAILS = 'view-misconfiguration-details',
  VIEW_USERS_BY_RISK = 'view-users-by-risk',
  VIEW_HUMAN_RISKS_BY_RISKS = 'view-human-risks-by-risks',
  VIEW_HUMAN_RISKS_BY_USERS = 'view-human-risks-by-users',
  VIEW_INCIDENTS_BY_DEVICE = 'view-incidents-by-device',
  VIEW_INCIDENTS_BY_DEVICE_CLOUD = 'view-incidents-by-device-cloud',
  VIEW_INCIDENTS_BY_USER = 'view-incidents-by-user',
  VIEW_EVENTS_AND_ALERTS_BY_DEVICE = 'view-events-and-alerts-by-device',
  VIEW_EVENTS_AND_ALERTS_BY_USER = 'view-events-and-alerts-by-user',

  IGNORE_MISCONFIGURATIONS = 'ignore-misconfigurations',
  RESTORE_MISCONFIGURATIONS = 'restore-misconfigurations',

  IGNORE_HUMAN_RISKS = 'ignore-human-risks',
  RESTORE_HUMAN_RISKS = 'restore-human-risks',

  IGNORE_APPLICATIONS = 'ignore-applications',
  RESTORE_APPLICATIONS = 'restore-applications',

  IGNORE_DEVICES = 'ignore-devices',
  RESTORE_DEVICES = 'restore-devices',

  IGNORE_USERS = 'ignore-users',
  RESTORE_USERS = 'restore-users',

  FIX_MISCONFIGURATIONS = 'fix-misconfigurations',
  FIX_MISCONFIGURATIONS_INLINE = 'fix-misconfigurations-inline',
  ROLLBACK_MISCONFIGURATIONS = 'rollback-misconfigurations',
  ROLLBACK_MISCONFIGURATIONS_INLINE = 'rollback-misconfigurations-inline',
  ROLLBACK_DEVICES = 'rollback-devices',
  ROLLBACK_DEVICES_INLINE = 'rollback-devices-inline',

  PATCH_APP = 'patch-app',

  ISOLATE_DEVICE = 'isolate-device',
  ISOLATE_DEVICE_INLINE_MENU = 'isolate-device-inline-menu',
  REMOVE_FROM_ISOLATION = 'remove-from-isolation',

  DEVICE_DETAILS_CHANGED = 'device-details-changed',
  DEVICE_DETAILS_SHOW_EXTRA_DATA = 'device-details-show-extra-data',
  DEVICE_EXTRA_DETAILS_CHANGED = 'device-extra-details-changed',

  USER_DETAILS_CHANGED = 'user-details-changed',
  USER_DETAILS_SHOW_EXTRA_DATA = 'user-details-show-extra-data',
  USER_EXTRA_DETAILS_CHANGED = 'user-extra-details-changed',
}

export interface SideMenuButton {
  text: string;
  handler: Function | string;
  disabled?: boolean;
  id: string;
  tooltiptext?: string;
}

type CreateActionDialogReturn = {
  confirmButton: GzButtonWebComponent;
};

type ChangeRiskScanOptionEvent = CustomEvent<{
  value: 'scanEndpoints' | 'scanCloud' | 'scanNSVA';
  checked: boolean;
}>;

interface RiskScanResponse {
  scanEndpoints: RiskScanResponseCodes;
  scanCloud: RiskScanResponseCodes;
  scanNSVA: RiskScanResponseCodes;
}

export function createActionDialog(
  dialogConfig,
  viewElements,
  requestPayload,
  toastMessages,
  isTask = false,
  shouldRefresh = false
): CreateActionDialogReturn {
  const { dialogTitle, dialogBodyMessage, confirmButtonText } = dialogConfig;
  const { grid, sideMenu } = viewElements;

  const dialogBody = document.createElement('div');
  // Allow for raw HTML or nodes to be passed in
  if (typeof dialogBodyMessage === 'string') {
    dialogBody.innerHTML = dialogBodyMessage;
  } else {
    dialogBody.appendChild(dialogBodyMessage);
  }

  const dialogFooter = document.createElement('div');
  const confirmButton = new GzButtonWebComponent();
  const cancelButton = new GzButtonWebComponent();
  confirmButton.innerText = confirmButtonText;
  cancelButton.innerText = translateFn('Cancel');
  cancelButton.setAttribute('type', 'outline');
  cancelButton.setAttribute('color', 'secondary');
  dialogFooter.appendChild(confirmButton);
  dialogFooter.appendChild(cancelButton);

  const dialogConfigs: GzDialog = {
    headline: dialogTitle,
    bodyContent: dialogBody,
    footerContent: dialogFooter,
    isOpen: true,
  };
  const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

  confirmButton.addEventListener('click', async () => {
    if (confirmButton.showSpinner) {
      return;
    }

    try {
      confirmButton.showSpinner = true;
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.ENDPOINTS,
        endpoint: 'RiskManagementActions',
        method: 'performAction',
        payload: requestPayload,
      });

      if (requestPayload.parameters.actionType === Actions.SCAN) {
        handleRiskScanActionResponse(response);
        return;
      }

      if (!isTask) {
        GzToastNotificationWebComponent.closeAllToasts();
        showToast(ToastSeverity.SUCCESS, toastMessages.success);
        return;
      }

      showToastForTask(toastMessages.success);
    } catch (error) {
      console.error(error);
      GzToastNotificationWebComponent.closeAllToasts();

      showToast(ToastSeverity.ERROR, toastMessages.error);
    } finally {
      dialog.close();
      grid.controller.resetSelection();
      if (!isTask || shouldRefresh) {
        sideMenu.closeSideMenu();
        await grid.controller.handleGridItemsLoad(true, true);
      }
    }
  });

  cancelButton.addEventListener('click', () => {
    dialog.close();
  });

  return {
    confirmButton,
  };
}

export function createFratDialog() {
  const dialogBody = document.createElement('div');
  dialogBody.innerHTML = `
      ${renderDialogText(
        translateFn(
          'FRAT does not include remediation actions. You must upgrade to a full-product license to unlock them.<br><br>Contact your sales representative for more details.'
        )
      )}
  `;

  const dialogFooter = document.createElement('div');
  const confirmButton = new GzButtonWebComponent();
  confirmButton.innerText = translateFn('Got it');
  dialogFooter.appendChild(confirmButton);

  const dialogConfigs: GzDialog = {
    headline: translateFn('Your current license does not provide access to this feature.'),
    bodyContent: dialogBody,
    footerContent: dialogFooter,
    isOpen: true,
  };
  const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

  confirmButton.addEventListener('click', async () => {
    dialog.close();
  });
}

export function renderDialogText(text: string) {
  if (isEmpty(text)) {
    return '';
  }

  return `
    <gz-text fontSize="${FontSizes.HEADING_6}">
      ${text}
    </gz-text>`;
}

export function renderRiskScanOptions(
  cspmAvailable: boolean,
  nsvaAvailable: boolean,
  changeHandler: (event: ChangeRiskScanOptionEvent) => void
): HTMLElement {
  const optionsDiv = document.createElement('div');

  let showCheckboxes = false;
  if (cspmAvailable) {
    const cloudFormField = new GzFormFieldWebComponent();

    const cloudCheckbox = new GzCheckboxWebComponent();
    cloudCheckbox.setAttribute('gz-form-input', '');
    cloudCheckbox.label = translateFn('Cloud resources scan');
    cloudCheckbox.value = 'scanCloud';
    cloudCheckbox.checked = true;
    cloudCheckbox.addEventListener('change', changeHandler);

    cloudFormField.appendChild(cloudCheckbox);

    optionsDiv.appendChild(cloudFormField);
    showCheckboxes = true;
  }

  if (nsvaAvailable) {
    const nsvaFormField = new GzFormFieldWebComponent();

    const nsvaCheckbox = new GzCheckboxWebComponent();
    nsvaCheckbox.setAttribute('gz-form-input', '');
    nsvaCheckbox.label = translateFn('Network scan');
    nsvaCheckbox.value = 'scanNSVA';
    nsvaCheckbox.checked = true;
    nsvaCheckbox.addEventListener('change', changeHandler);

    nsvaFormField.appendChild(nsvaCheckbox);
    optionsDiv.appendChild(nsvaFormField);

    showCheckboxes = true;
  }

  if (!showCheckboxes) {
    return optionsDiv;
  }

  const endpointsFormField = new GzFormFieldWebComponent();
  endpointsFormField.className = 'mt-4 mb-2';

  const endpointsCheckbox = new GzCheckboxWebComponent();
  endpointsCheckbox.setAttribute('gz-form-input', '');
  endpointsCheckbox.label = translateFn('Endpoints scan');
  endpointsCheckbox.value = 'scanEndpoints';
  endpointsCheckbox.checked = true;
  endpointsCheckbox.addEventListener('change', changeHandler);

  endpointsFormField.appendChild(endpointsCheckbox);
  optionsDiv.prepend(endpointsFormField);

  return optionsDiv;
}

export function renderSideMenuSectionTitle(title: string) {
  return html`
    <gz-title
      fontSize="${FontSizes.PARAGRAPH_11}"
      fontWeight="${FontWeights.SEMIBOLD}"
      class="section-title text-uppercase mb-2"
      >${title}
    </gz-title>
  `;
}

export function renderSideMenuCompanyIfNeeded(companyName: string): TemplateResult | null {
  if (Application.Main.company.isPartner()) {
    return html`
      <gz-labeled-content label="${translateFn('Company')}">
        <gz-tooltip isEllipsis class="w-100" tooltiptext="${companyName}">
          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="d-inline">
            ${companyName}
          </gz-text>
        </gz-tooltip>
      </gz-labeled-content>
    `;
  }

  return null;
}

export function renderSideMenuRiskScore(item, overlay, riskType): TemplateResult {
  return html`
    <gz-labeled-content label="${translateFn('Risk score')}">
      ${renderRiskScore(item, overlay, riskType)}
    </gz-labeled-content>
  `;
}

export function renderLastScannedOn(lastScan: any) {
  return html` <gz-labeled-content label="${translateFn('Last scanned on')}">
    ${lastScan ? moment(lastScan).format('DD MMM YYYY, HH:mm') : '-'}
  </gz-labeled-content>`;
}

export function renderRiskScoreWithoutBreakdown(score: number) {
  const text = getTextForScore(score);

  const color = getBulletColorForScore(score);

  if (score > 0) {
    return html` <gz-labeled-content label="${translateFn('Risk score')}">
      <gz-bullet color="${color}"><span class="cell-label">${text} (${score}%)</span></gz-bullet>
    </gz-labeled-content>`;
  } else {
    return html` <gz-labeled-content label="${translateFn('Risk score')}">
      <gz-bullet color="${color}"><span class="cell-label">${score}%</span></gz-bullet>
    </gz-labeled-content>`;
  }
}

export function renderSideMenuStatus(status: Status): TemplateResult {
  return html` <gz-labeled-content label="${translateFn('Status')}">
    ${BaseWebComponentTranslator.getInstance().getRiskManagementStatus(status)}
  </gz-labeled-content>`;
}

export function renderSideMenuState(state: State): TemplateResult {
  return html` <gz-labeled-content label="${translateFn('State')}">
    ${BaseWebComponentTranslator.getInstance().getRiskManagementState(state)}
  </gz-labeled-content>`;
}

export function renderSideMenuWatchlist(watchlist: Watchlist): TemplateResult {
  return html` <gz-labeled-content label="${translateFn('In watchlist')}">
    ${BaseWebComponentTranslator.getInstance().getRiskManagementWatchlist(watchlist)}
  </gz-labeled-content>`;
}

export function renderSideMenuOperatingSystem(operatingSystem: OperatingSystemType): TemplateResult {
  let icon: GzIconWebComponent | symbol = nothing;

  if (operatingSystem !== OperatingSystemType.UNKNOWN) {
    icon = new GzIconWebComponent();
    icon.name = `ds-${OS_CLASS.get(operatingSystem)}`;
    icon.size = 20;
    icon.color = ButtonColor.SECONDARY;
    icon.setCustomIconSet(entityIconSet);
  }

  return html` <gz-labeled-content label="${translateFn('Platform')}">
    <div class="d-flex align-items-center" style="gap: 4px">
      ${icon}
      ${BaseWebComponentTranslator.getInstance().getOperatingSystem(operatingSystem)}
    </div>
  </gz-labeled-content>`;
}

export function renderSideMenuOperatingSystems(operatingSystems: OperatingSystemType[]): TemplateResult {
  operatingSystems = operatingSystems ?? [];

  const joinedOperatingSystems =
    operatingSystems.length > 0
      ? operatingSystems.map(os => BaseWebComponentTranslator.getInstance().getOperatingSystem(os)).join(', ')
      : '-';

  return html` <gz-labeled-content label="${translateFn('Platforms')}">
    <gz-tooltip tooltiptext="${joinedOperatingSystems}" clampLine isEllipsis> ${joinedOperatingSystems}</gz-tooltip>
  </gz-labeled-content>`;
}

export function renderSideMenuMitigationType(mitigation: MitigationType): TemplateResult {
  return html`
    <gz-labeled-content label="${translateFn('Mitigation type')}">
      ${BaseWebComponentTranslator.getInstance().getRiskManagementMitigationType(mitigation)}
    </gz-labeled-content>
  `;
}

export function renderSideMenuCompliance(compliance: Compliance[]) {
  if (!compliance || compliance.length === 0) {
    return nothing;
  }

  const joinedCompliance = compliance
    .map(complianceId => BaseWebComponentTranslator.getInstance().getRiskManagementCompliance(complianceId))
    .join(', ');

  return html`
    <gz-labeled-content label="${translateFn('Compliance')}">
      <gz-tooltip isEllipsis tooltiptext="${joinedCompliance}" clampLine>
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="d-inline">
          ${joinedCompliance}
        </gz-text>
      </gz-tooltip>
    </gz-labeled-content>
  `;
}

export function renderButtonGroup(buttons: Array<SideMenuButton>): TemplateResult {
  return html` <div class="group-buttons pt-1">${buttons.map(button => renderButton(button))}</div> `;
}

export function renderButton({ text, handler, disabled, id, tooltiptext = '' }: SideMenuButton) {
  switch (typeof handler) {
    case 'function':
      return html` <gz-tooltip tooltiptext="${disabled ? tooltiptext : ''}">
        <gz-button
          type="outline"
          color="primary"
          buttonHeight="small"
          buttonfontsize="small"
          lowercase="true"
          @click="${handler}"
          .disabled=${disabled}
          id="${id}"
        >
          ${text}
        </gz-button>
      </gz-tooltip>`;
    case 'string':
      return html`<gz-tooltip tooltiptext="${disabled ? tooltiptext : ''}">
        <gz-link
          href="${handler}"
          target="${Target.SELF}"
          type="${LinkType.BUTTON}"
          color="${LinkColor.PRIMARY}"
          fill="${ButtonFill.OUTLINE}"
          .disabled=${disabled}
          id="${id}"
        >
          ${text}
        </gz-link>
      </gz-tooltip>`;
    default:
      throw Error('Unsupported handler type: ' + (typeof handler));
  }
}

export function renderLink(
  text: string,
  handler: Function | string,
  hasSeparator = false,
  disabled = false,
  customTooltipText = ''
) {
  const callback = (typeof handler === 'function') ? handler : undefined;
  const url = typeof handler === 'string' ? handler : undefined;
  const target = typeof url === 'string' ? Target.SELF : undefined;

  return html`<gz-tooltip
    class="${hasSeparator ? 'separator' : ''}"
    tooltipText="${!customTooltipText ? text : customTooltipText}"
    ?isEllipsis="${!customTooltipText}"
  >
    <gz-link
      type="quiet"
      fontSize="${FontSizes.PARAGRAPH_11}"
      color="primary"
      class="text-uppercase d-inline"
      buttonHeight="small"
      lowercase=${true}
      @click="${ifDefined(callback)}"
      href="${ifDefined(url)}"
      target="${ifDefined(target)}"
      ?disabled="${disabled}"
    >
      ${text}
    </gz-link>
  </gz-tooltip>`;
}

export function renderCategories(iconName: string, text: string, number: string, handler: Function): TemplateResult {
  return html` <div class="d-flex flex-row align-items-center w-full category-container pl-1" @click=${handler}>
    <gz-icon name="${iconName}" class="pr-1" color="secondary"></gz-icon>
    <div class="d-flex flex-row flex-1 align-items-center text-container">
      <gz-tooltip class="w-auto" tooltipText="${text}" isEllipsis>
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="d-inline w-auto text-uppercase"
          >${text}
        </gz-text>
      </gz-tooltip>
      <gz-text fontSize="${FontSizes.PARAGRAPH_11}" class="d-inline number flex-0">${number}</gz-text>
    </div>
    <gz-icon name="ds-icon-arrow_right_expand" size="26" color="secondary"></gz-icon>
  </div>`;
}

export function renderRegions(regions: string[]) {
  if (!regions || regions.length === 0) {
    return nothing;
  }

  return html`
    <gz-labeled-content label="${translateFn('Regions')}">
      <gz-tooltip tooltiptext="${regions.join(', ')}" clampLine isEllipsis>${regions.join(', ')}</gz-tooltip>
    </gz-labeled-content>
  `;
}

export function handleWatchlistChangeButtonClick(): void {
  if (!this.watchlistButton) {
    return;
  }

  this.watchlistButton.setLoadingState(true);

  const event = new CustomEvent(ActionsEvents.WATCHLIST_CHANGED, {
    detail: {
      ids: [this.item._id],
      newWatchlist: this.item.watchlist === Watchlist.YES ? Watchlist.NO : Watchlist.YES,
      companyId: this.item.companyId,
    },
    bubbles: true,
    composed: true,
  });

  this.dispatchEvent(event);
}

/**
 *
 * @param eventData
 * @param gridComponent
 * @param sideMenuComponent
 */
export function buildPatchAppAction(eventData, gridComponent, sideMenuComponent) {
  const {
    endpointId,
    applicationIdToPatchIdMapping,
    company,
    fullApplicationName,
    patchesCount,
    patchProductId,
    patchProductName,
    totalEndpoints,
    patchableEndpoints,
    patchName,
  } = eventData.detail;

  if (patchesCount > 1) {
    // when multiple patches, then redirect to patch inventory
    app.getModuleByName('patchInventory').showPatchInventoryForProduct(patchProductId, patchProductName, company);

    return;
  }

  let dialogBodyMessage = `${renderDialogText(
    translateFn('Apply patch? | {PATCH_NAME} is the latest identified patch that resolves your vulnerability.', {
      PATCH_NAME: patchName,
    })
  )}`;

  dialogBodyMessage += `${renderDialogText(translateFn('Applying this patch may impact other CVEs and products.'))}`;

  dialogBodyMessage += ` ${renderDialogText(
    translateFn('{BOLD}Patch name: {PATCH_NAME} {BOLD_END}', {
      BOLD: '<strong>',
      BOLD_END: '</strong>',
      PATCH_NAME: patchName,
    })
  )}`;

  if (totalEndpoints !== patchableEndpoints) {
    dialogBodyMessage += `${renderDialogText(
      translateFn(
        'You are about to install the latest patch which resolves your vulnerability on {PATCHABLE_ENDPOINTS} out of {TOTAL_ENDPOINTS} endpoints detected. You must install the Patch Management module on the remaining endpoints to install this patch.',
        {
          PATCHABLE_ENDPOINTS: patchableEndpoints,
          TOTAL_ENDPOINTS: totalEndpoints,
        }
      )
    )}`;
  }

  const requestPayload = {
    parameters: {
      actionType: Actions.INSTALL_PATCH,
      applicationIdToPatchIdMapping,
      companyId: company.id,
    },
  };

  if (endpointId) {
    requestPayload['parameters']['endpointId'] = endpointId;
  }

  createActionDialog(
    {
      dialogTitle: translateFn('Patch {applicationName}', { applicationName: fullApplicationName }),
      confirmButtonText: translateFn('Install patch'),
      dialogBodyMessage,
    },
    {
      grid: gridComponent,
      sideMenu: sideMenuComponent,
    },
    requestPayload,
    {
      success: translateFn('Task created successfully.'),
      error: translateFn('Failed to create task.'),
    },
    true
  );
}

export function getEndpointIdsFromAffectedDevices(affectedDevices) {
  let firstEndpointIdFromAffectedDevice = null;
  const allEndpointsIds: any = [];
  affectedDevices.map(affectedDevicesGroup => {
    if (!firstEndpointIdFromAffectedDevice) {
      if (affectedDevicesGroup.ids.length > 0) {
        firstEndpointIdFromAffectedDevice = affectedDevicesGroup.ids[0];
      }
    }

    if (affectedDevicesGroup.ids.length > 0) {
      allEndpointsIds.push(...affectedDevicesGroup.ids);
    }
  });

  return {
    firstEndpointIdFromAffectedDevice,
    allEndpointsIds,
  };
}

export function getIconNameItemType(type): string {
  switch (type) {
    case FindingsDetailsItemsType.PATHS:
      return 'ds-icon-path';
    case FindingsDetailsItemsType.REGISTRIES:
      return 'ds-icon-registry';
    case FindingsDetailsItemsType.HOSTS:
      return 'ds-icon-host';
    case FindingsDetailsItemsType.SHARED_PASSWORDS:
      return 'ds-icon-password';
    case FindingsDetailsItemsType.DETECTIONS:
      return 'ds-icon-detection';
    case FindingsDetailsItemsType.PACKAGES:
      return 'ds-icon-packages';
    case FindingsDetailsItemsType.GENERIC:
      return 'ds-icon-resource_generic';
    default:
      return 'ds-icon-resource_generic';
  }
}

export async function applyDeviceDetailsChangedEventOnSideComponent(
  sideMenuComponent,
  extraDetailsType,
  extraData: DeviceDetailsChangedEvent
) {
  if (!sideMenuComponent.deviceDetailsComponent) {
    return;
  }

  setLoadingStateForDeviceDetails(sideMenuComponent, true);

  try {
    const deviceDataResponse = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'RiskManagementDevices',
      method: 'getExtraDetailsForDevice',
      payload: {
        companyId: extraData.companyId,
        endpointId: extraData.endpointId,
        type: extraDetailsType,
        uniqueIdentifierForFinding: extraData.uniqueIdentifierForFinding,
      },
    });

    setLoadingStateForDeviceDetails(sideMenuComponent, false);

    if (
      deviceDataResponse &&
      Object.keys(deviceDataResponse).length > 0 &&
      Object.keys(deviceDataResponse['specifics']).length > 0
    ) {
      sideMenuComponent.deviceDetails = deviceDataResponse['specifics'];
    } else {
      sideMenuComponent.deviceDetails = {};
    }
  } catch (e) {
    setLoadingStateForDeviceDetails(sideMenuComponent, false);
    setErrorStateForDeviceDetails(sideMenuComponent);
  }
}

export function setLoadingStateForDeviceDetails(sideMenuComponent, state) {
  sideMenuComponent.deviceDetailsComponent.deviceDetailsDropdown.disabled = state;

  if (state) {
    sideMenuComponent.deviceDetailsComponent.deviceInformation = null;
  }
}

export function setErrorStateForDeviceDetails(sideMenuComponent) {
  sideMenuComponent.deviceDetailsComponent.hasErrors = true;
}

export function setErrorStateForDeviceExtraDetails(sideMenuComponent) {
  sideMenuComponent.deviceExtraDetailsComponent.hasErrors = true;
}

export async function applyDeviceExtraDetailsChangedEventOnSideComponent(
  sideMenuComponent,
  extraDetailsType,
  extraData: DeviceExtraDetailsChangedEvent
) {
  if (!sideMenuComponent.deviceExtraDetailsComponent) {
    return;
  }

  setLoadingStateForDeviceExtraDetails(sideMenuComponent, true);

  try {
    const deviceDataResponse = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'RiskManagementDevices',
      method: 'getExtraDetailsForDevice',
      payload: {
        companyId: extraData.companyId,
        endpointId: extraData.endpointId,
        type: extraDetailsType,
        uniqueIdentifierForFinding: extraData.uniqueIdentifierForFinding,
      },
    });

    if (
      deviceDataResponse &&
      Object.keys(deviceDataResponse).length > 0 &&
      Object.keys(deviceDataResponse['specifics']).length > 0 &&
      deviceDataResponse['specifics'].hasOwnProperty(extraData.itemsType)
    ) {
      sideMenuComponent.deviceExtraDetails = {
        items: deviceDataResponse['specifics'][extraData.itemsType],
        itemsType: extraData.itemsType,
      };
    } else {
      sideMenuComponent.deviceExtraDetails = {
        items: [],
        itemsType: extraData.itemsType,
      };
    }

    setLoadingStateForDeviceExtraDetails(sideMenuComponent, false);
  } catch (e) {
    setLoadingStateForDeviceExtraDetails(sideMenuComponent, false);
    setErrorStateForDeviceExtraDetails(sideMenuComponent);
  }
}

export function setLoadingStateForDeviceExtraDetails(sideMenuComponent, state) {
  sideMenuComponent.deviceExtraDetailsComponent.deviceDetailsDropdown.disabled = state;

  if (state) {
    sideMenuComponent.deviceExtraDetailsComponent.items = null;
  }
}

export function applyDetailsToShowExtraData(sideMenuComponent, details: ShowExtraDataEventDetails) {
  sideMenuComponent.showExtraDetailsPanel = details.showExtraDetailsPanel;

  sideMenuComponent.deviceExtraDetailsInitialDropdown = details.dropdownDetails;
  sideMenuComponent.deviceExtraDetails = {
    items: details.items,
    itemsType: details.itemsType,
    companyId: details.companyId,
  };
}

export function getFindingsDetailsItemType(type) {
  switch (type) {
    case FindingsDetailsItemsType.PATHS:
      return translateFn('Paths');
    case FindingsDetailsItemsType.REGISTRIES:
      return translateFn('Registries');
    case FindingsDetailsItemsType.HOSTS:
      return translateFn('Hosts');
    case FindingsDetailsItemsType.SHARED_PASSWORDS:
      return translateFn('Shared passwords');
    case FindingsDetailsItemsType.DETECTIONS:
      return translateFn('Detection names');
    case FindingsDetailsItemsType.PACKAGES:
      return translateFn('Packages');
    case FindingsDetailsItemsType.GENERIC:
      return translateFn('Generic');
    default:
      return translateFn('N/A');
  }
}

export function getUserIdsFromAffectedUsers(affectedUsers) {
  let firstUserIdFromAffectedUsers = null;
  const allUserIds: any = [];
  affectedUsers.map(affectedUsersGroup => {
    if (!firstUserIdFromAffectedUsers) {
      if (affectedUsersGroup.ids.length > 0) {
        firstUserIdFromAffectedUsers = affectedUsersGroup.ids[0];
      }
    }

    if (affectedUsersGroup.ids.length > 0) {
      allUserIds.push(...affectedUsersGroup.ids);
    }
  });

  return {
    firstUserIdFromAffectedUsers,
    allUserIds,
  };
}

export function renderEmptyDeviceDetailsState(hasDeviceDropdown: boolean) {
  const moreInfoText = hasDeviceDropdown
    ? translateFn('No information available for the selected resource.')
    : translateFn('No information available for this resource.');

  return html`
    <gz-image-repo state="${ImageState.EMPTY}">
      <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}">
        ${translateFn('No results found.')}
      </gz-title>
      <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
        ${moreInfoText}
      </gz-text>
    </gz-image-repo>`;
}

export function renderErrorDeviceDetailsState() {
  return html` <gz-image-repo state="${ImageState.ERROR}">
    <gz-text fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}"
      >${translateFn(
        'Data loading has failed. Please refresh the page. If the issue persists, contact Bitdefender Enterprise Support.'
      )}
    </gz-text>
  </gz-image-repo>`;
}

export function setFilterOptionsForDeviceDropdown(component) {
  if (!component.deviceDetailsDropdown || !component.initialDropdownDetails) {
    return;
  }

  // This will fetch other suggested endpoints name in dropdown menu
  component.deviceDetailsDropdown
    .setFilterOptions(
      {
        items: [
          {
            label: component.initialDropdownDetails.endpointName,
            value: component.initialDropdownDetails.endpointId,
          },
        ],
        totalItemsCount: 1,
      },
      false,
      {
        companyId: component.initialDropdownDetails.companyId,
        endpointIds: component.initialDropdownDetails.endpointIds,
      }
    )
    .then(() => {
      if (component.initialDropdownDetails) {
        component.deviceDetailsDropdown.value = component.initialDropdownDetails.endpointId;
      }
    })
    .catch();
}

export function renderEmptyUserDetailsState(hasUserDropdown: boolean) {
  const moreInfoText = hasUserDropdown
    ? translateFn('No information available for the selected identity.')
    : translateFn('No information available for this identity.');

  return html`
    <gz-image-repo state="${ImageState.EMPTY}">
      <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}">
        ${translateFn('No results found.')}
      </gz-title>
      <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">${moreInfoText}</gz-text>
    </gz-image-repo>`;
}

export function renderErrorUserDetailsState() {
  return html` <gz-image-repo state="${ImageState.ERROR}">
    <gz-text fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}"
      >${translateFn(
        'Data loading has failed. Refresh the page, and if the issue persists, contact Bitdefender Enterprise Support for more information.'
      )}
    </gz-text>
  </gz-image-repo>`;
}

export function setFilterOptionsForUserDropdown(component) {
  if (!component.userDetailsDropdown || !component.initialDropdownDetails) {
    return;
  }

  // This will fetch other suggested usernames in dropdown menu
  component.userDetailsDropdown
    .setFilterOptions(
      {
        items: [
          {
            label: component.initialDropdownDetails.userName,
            value: component.initialDropdownDetails.userId,
          },
        ],
        totalItemsCount: 1,
      },
      false,
      {
        companyId: component.initialDropdownDetails.companyId,
        userIds: component.initialDropdownDetails.userIds,
      }
    )
    .then(() => {
      if (component.initialDropdownDetails) {
        component.userDetailsDropdown.value = component.initialDropdownDetails.userId;
      }
    })
    .catch();
}

export function overlayOpenListener() {
  if (document.body.style.pointerEvents === 'none') {
    document.body.style.pointerEvents = 'auto';
  }
}

export function getOptionsForInlineMenu(itemsType: Number | null) {
  const inlineMenuItems = [
    {
      title: translateFn('Copy to clipboard'),
      value: InlineMenuExtraDetailsSelectEvents.copyToClipboard,
      disabled: false,
    },
  ];

  if (itemsType !== FindingsDetailsItemsType.GENERIC && itemsType !== FindingsDetailsItemsType.PACKAGES) {
    inlineMenuItems.push({
      title: translateFn('View incidents'),
      value: InlineMenuExtraDetailsSelectEvents.viewIncidents,
      disabled: false,
    });

    inlineMenuItems.push({
      title: translateFn('View events and alerts'),
      value: InlineMenuExtraDetailsSelectEvents.viewEvents,
      disabled: false,
    });
  }

  return inlineMenuItems;
}

export function renderAccordionHeader(title: string, score: number, state: State) {
  return html`
    <div
      slot="dynamic-title-content"
      class="cell-label min-width-zero d-flex flex-1 align-items-center accordion-title"
    >
      <div class="d-flex min-width-zero flex-1 justify-space-between">
        <gz-bullet color="${getBulletColorForScore(score)}" class="min-width-zero">
          <gz-tooltip tooltipText="${title}" isEllipsis>
            <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="d-inline"> ${title}</gz-text>
          </gz-tooltip>
        </gz-bullet>
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" class="flex-0 pr-1 pl-1 indicator-status">
          ${state === State.IGNORED ? translateFn('Ignored') : ''}
        </gz-text>
      </div>
    </div>
  `;
}

enum RiskScanResponseCodes {
  NOT_APPLICABLE = -1,
  RESULT_SUCCESS = 0,
  EXCEPTION_NO_TARGETS_FOUND = 1,
  EXCEPTION_ENDPOINTS_SCAN_FAILED = 2,
  EXCEPTION_CSPM_SCAN_FAILED = 3,
  EXCEPTION_NSVA_SCAN_FAILED = 4,
}

function handleRiskScanActionResponse(response: RiskScanResponse) {
  const { scanEndpoints, scanCloud, scanNSVA } = response;

  GzToastNotificationWebComponent.closeAllToasts();
  handleRiskScanOptionResponse(scanEndpoints, 'scanEndpoints');
  handleRiskScanOptionResponse(scanCloud, 'scanCloud');
  handleRiskScanOptionResponse(scanNSVA, 'scanNSVA');
}

function handleRiskScanOptionResponse(
  value: RiskScanResponseCodes,
  option: 'scanEndpoints' | 'scanCloud' | 'scanNSVA'
) {
  switch (value) {
    case RiskScanResponseCodes.NOT_APPLICABLE:
      return;
    case RiskScanResponseCodes.RESULT_SUCCESS:
      switch (option) {
        case 'scanCloud':
          showToast(
            ToastSeverity.SUCCESS,
            translateFn('Cloud resources scan task created successfully.')
          );
          break;
        case 'scanNSVA':
          showToastForTask(translateFn('Network scan task created successfully.'));
          break;
        case 'scanEndpoints':
        default:
          showToastForTask(translateFn('Endpoint scan task created successfully.'));
          break;
      }
      return;
    case RiskScanResponseCodes.EXCEPTION_NO_TARGETS_FOUND:
      switch (option) {
        case 'scanNSVA':
          showToast(ToastSeverity.ERROR, translateFn('No valid Network Sensor found.'));
          break;
        case 'scanEndpoints':
        default:
          showToast(ToastSeverity.ERROR, translateFn('Failed to create task. No valid targets found.'));
          break;
      }
      return;
    case RiskScanResponseCodes.EXCEPTION_ENDPOINTS_SCAN_FAILED:
      showToast(ToastSeverity.ERROR, translateFn('Failed to create the risk scan task on target endpoints.'));
      return;
    case RiskScanResponseCodes.EXCEPTION_CSPM_SCAN_FAILED:
      showToast(ToastSeverity.ERROR, translateFn('Failed to create the risk scan task on target cloud resources.'));
      return;
    case RiskScanResponseCodes.EXCEPTION_NSVA_SCAN_FAILED:
      showToast(ToastSeverity.ERROR, translateFn('Failed to create risk scan task for network vulnerabilities.'));
      return;
  }
}

function showToastForTask(message: string) {
  GzToastNotificationWebComponent.buildToastWrapperElement({
    message: message,
    severity: ToastSeverity.SUCCESS,
    actionButtonText: translateFn('View status.'),
    hasAction: true,
    actionButtonMode: ToastActionButtonMode.LINK,
    actionCallback: () => {
      navigateToTasksSection();
      GzToastNotificationWebComponent.closeAllToasts();
    },
  });
}
